/* eslint-disable canonical/filename-match-exported */
import { mergeProps } from '@react-aria/utils'
import { useMemo, useState, useEffect } from 'react'
import type { MutableRefObject } from 'react'

import { useComboboxList } from '@ui/Combobox/ComboboxList/context'
import ComboboxListItem from '@ui/Combobox/ComboboxListItem'
import { useComboboxTrigger } from '@ui/Combobox/ComboboxProvider'
import { useIsVisible } from '@ui/Menu/MenuList/utils/useIsVisible'
import Typography from '@ui/Typography'
import VirtualList from '@ui/VirtualList/VirtualList'

import * as styles from './VirtualizedComboboxListContent.css'

export default function ComboboxListContent() {
  const { state, comboboxListRef, listBoxProps, setIsVisible } = useComboboxList()

  const { comboboxProps } = useComboboxTrigger()

  const mergedProps = mergeProps(listBoxProps, comboboxProps)

  useIsVisible(comboboxListRef, setIsVisible)

  // Dynamically measure the height of a ComboboxListItem for virtualization
  const [itemHeight, setItemHeight] = useState(32) // Default to a reasonable height

  // Convert collection items to virtual list compatible items
  const data = Array.from(state.collection).map((item) => ({
    id: item.key.toString(),
    ...item,
  }))

  // Measure the height of a list item after mount
  useEffect(() => {
    if (data.length > 0) {
      const listItems = document.querySelectorAll('.combobox-list-item')
      if (listItems && listItems.length > 0) {
        const firstItem = listItems[0] as HTMLElement
        const height = firstItem.getBoundingClientRect().height
        if (height > 0) {
          setItemHeight(height)
        }
      }
    }
  }, [data.length])

  // We need to calculate height since we're using virtualization and it relies on a height of its parent container
  const containerHeight = useMemo(() => {
    const padding = 8 // Extra padding (4px top + 4px bottom)

    // Calculate total height based on number of items + padding
    const totalHeight = data.length * itemHeight + padding

    return totalHeight
  }, [data.length, itemHeight])

  return (
    <div
      className={styles.virtualizedContainer}
      style={{ height: `${containerHeight}px` }}
    >
      <VirtualList
        data={data}
        context={{
          list: {
            ref: comboboxListRef as unknown as MutableRefObject<HTMLDivElement>,
            props: { ...mergedProps, className: styles.root },
            role: 'grid',
          },
        }}
        itemContent={(_, item) => {
          if (item.type === 'item') {
            return <ComboboxListItem item={item} key={item.key} />
          }
        }}
        components={{
          EmptyPlaceholder: () => (
            <Typography
              component="li"
              variant="caption1"
              color="textTertiary"
              className={styles.empty}
            >
              No results found
            </Typography>
          ),
        }}
      />
    </div>
  )
}
